/**
 * The combat event ring (plan §2.5): one preallocated SoA ring written by the sim and read by any number
 * of independent cursors (fx, audio, HUD, ship views, AI). Allocation-free (D-014).
 *
 * Reader pattern:
 * ```ts
 * let cursor = 0
 * // each frame or tick:
 * for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
 *   const s = i & EVENT_MASK
 *   if (ring.type[s] === EV.kill) { ... ring.eid[s] ... }
 * }
 * cursor = ring.head
 * ```
 */

export const EVENT_CAPACITY = 1024
export const EVENT_MASK = EVENT_CAPACITY - 1

/**
 * Event types. Field conventions (unused fields are 0, or -1 for entity fields):
 * - `fire`: x/y/z muzzle (world), d* direction, eid shooter, weapon.
 * - `hitShield` / `hitHull`: x/y/z hit point, d* surface normal, eid victim, other attacker, weapon, amount = damage taken.
 * - `kill`: x/y/z victim position, eid victim, other attacker, weapon.
 * - `explode`: x/y/z centre, eid the exploding ship or -1 (torpedo), amount = size hint (ship bounds radius or blast radius).
 * - `torpedoLaunch`: x/y/z muzzle, d* direction, eid shooter, other target, weapon.
 * - `lockStart` / `locked` / `lockLost`: eid shooter, other target.
 * - `waveStart`: amount = wave number (1-based).
 * - `respawn` / `spawn`: x/y/z position, eid ship.
 * - `boostStart`: x/y/z position, d* forward, eid ship.
 * - `shieldDown`: x/y/z position, eid ship, other attacker.
 */
export const EV = {
  fire: 1,
  hitShield: 2,
  hitHull: 3,
  kill: 4,
  explode: 5,
  torpedoLaunch: 6,
  lockStart: 7,
  locked: 8,
  lockLost: 9,
  waveStart: 10,
  respawn: 11,
  boostStart: 12,
  shieldDown: 13,
  spawn: 14,
} as const
export type EventType = (typeof EV)[keyof typeof EV]

export interface EventRing {
  type: Uint8Array
  x: Float64Array
  y: Float64Array
  z: Float64Array
  dx: Float32Array
  dy: Float32Array
  dz: Float32Array
  eid: Int32Array
  other: Int32Array
  weapon: Uint8Array
  amount: Float32Array
  /** Total events ever emitted. Event `i` lives in slot `i & EVENT_MASK` while `i >= head - EVENT_CAPACITY`. */
  head: number
}

export function createEventRing(): EventRing {
  return {
    type: new Uint8Array(EVENT_CAPACITY),
    x: new Float64Array(EVENT_CAPACITY),
    y: new Float64Array(EVENT_CAPACITY),
    z: new Float64Array(EVENT_CAPACITY),
    dx: new Float32Array(EVENT_CAPACITY),
    dy: new Float32Array(EVENT_CAPACITY),
    dz: new Float32Array(EVENT_CAPACITY),
    eid: new Int32Array(EVENT_CAPACITY),
    other: new Int32Array(EVENT_CAPACITY),
    weapon: new Uint8Array(EVENT_CAPACITY),
    amount: new Float32Array(EVENT_CAPACITY),
    head: 0,
  }
}

/** Appends an event, overwriting the oldest when full. Returns the event's sequence number. */
export function emitEvent(
  ring: EventRing,
  type: EventType,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  eid: number,
  other: number,
  weapon: number,
  amount: number,
): number {
  const seq = ring.head
  const s = seq & EVENT_MASK
  ring.type[s] = type
  ring.x[s] = x
  ring.y[s] = y
  ring.z[s] = z
  ring.dx[s] = dx
  ring.dy[s] = dy
  ring.dz[s] = dz
  ring.eid[s] = eid
  ring.other[s] = other
  ring.weapon[s] = weapon
  ring.amount[s] = amount
  ring.head = seq + 1
  return seq
}

/** The first sequence number a reader at `cursor` can still read: a reader that fell more than a ring behind skips to the oldest event. */
export function firstReadable(ring: EventRing, cursor: number): number {
  const oldest = ring.head - EVENT_CAPACITY
  return cursor > oldest ? cursor : oldest > 0 ? oldest : 0
}

/** Ring slot of sequence number `i`. */
export function eventSlot(i: number): number {
  return i & EVENT_MASK
}
